import type { LigneMouvement, Mouvement } from '@mairistem/comptabilite';
import type { ErrorField } from '@mairistem/liste';
import { every, isEmpty, isNaN, isNil } from 'lodash';
import BaseProfilEcritureMouvement from './BaseProfilEcritureMouvement';

class BaseProfilRattachement extends BaseProfilEcritureMouvement {
	naturePieceOrigine = '';

	exerRattachement = '';

	idLigneOriginePes: number;

	idPieceOrigine: number;

	getLigneDefaultAttributes(): object {
		return {
			...super.getLigneDefaultAttributes(),
			naturePieceOrigine: this.naturePieceOrigine,
			exerRattachement: this.exerRattachement,
			idLigneOriginePes: this.idLigneOriginePes,
			idPieceOrigine: this.idPieceOrigine,
		};
	}

	onProfilSelect(data: Partial<Mouvement>) {
		return {
			...super.onProfilSelect(data),
			// Si les lignes n'ont pas déjà les informations
			...(!isNil(data.lignes) &&
				(!every(data?.lignes, this.getLigneDefaultAttributes()) ||
					every(this.getLigneDefaultAttributes(), (attributes) =>
						isEmpty(attributes),
					)) && {
					lignes: data?.lignes?.map((ligne) => ({
						...ligne,
						...this.getLigneDefaultAttributes(),
					})),
				}),
		};
	}

	checkLigne(ligne: LigneMouvement): ErrorField[] {
		return [
			...super.checkLigne(ligne),
			...this.checkExerRattachementFormat(ligne),
		];
	}

	// #region Contrôles de ligne réutilisables
	// Chaque contrôle est isolé dans une méthode dédiée afin que les profils se
	// contentent de composer les règles qui les concernent (cf. checkLigne).

	/** L'exercice de rattachement doit être une année valide (4 chiffres). */
	checkExerRattachementFormat(ligne: LigneMouvement): ErrorField[] {
		if (
			ligne.exerRattachement.length !== 4 ||
			isNaN(Number(ligne.exerRattachement))
		) {
			return [
				{
					id: `ligneMouvement-exerRattachement-${ligne?.identifiant}`,
					message:
						"L'exercice de rattachement doit être une année valide (4 chiffres)",
				},
			];
		}

		return [];
	}

	/**
	 * L'exercice de rattachement doit correspondre à un exercice antérieur ou
	 * égal à celui du mouvement.
	 */
	checkExerRattachementAnterieurOuEgal(ligne: LigneMouvement): ErrorField[] {
		if (
			parseInt(ligne.exerRattachement, 10) > parseInt(this.exerRattachement, 10)
		) {
			return [
				{
					id: 'mouvement-exerRattachement',
					message:
						"L'exercice de rattachement doit correspondre à un exercice antérieur ou égal à celui du mouvement",
				},
			];
		}

		return [];
	}

	/** L'exercice de rattachement doit être identique à celui du mouvement. */
	checkExerRattachementIdentique(ligne: LigneMouvement): ErrorField[] {
		if (
			parseInt(ligne?.exerRattachement, 10) !==
			parseInt(this.exerRattachement, 10)
		) {
			return [
				{
					id: `ligneMouvement-exerRattachement-${ligne?.identifiant}`,
					message:
						"L'exercice de rattachement doit être identique à l'exercice du mouvement",
				},
			];
		}

		return [];
	}

	/** L'exercice de rattachement doit être différent de celui du mouvement. */
	checkExerRattachementDifferent(ligne: LigneMouvement): ErrorField[] {
		if (
			parseInt(ligne?.exerRattachement, 10) ===
			parseInt(this.exerRattachement, 10)
		) {
			return [
				{
					id: `ligneMouvement-exerRattachement-${ligne?.identifiant}`,
					message:
						"L'exercice de rattachement doit être différent de l'exercice du mouvement",
				},
			];
		}

		return [];
	}

	/** L'identifiant de la pièce d'origine doit être renseigné (supérieur ou égal à 1). */
	checkPieceOrigineRenseignee(ligne: LigneMouvement): ErrorField[] {
		if (Number(ligne.pieceOrigine) < 1) {
			return [
				{
					id: `ligneMouvement-idPieceOrigine-${ligne?.identifiant}`,
					message:
						"L'identifiant de la pièce d'origine doit être supérieur ou égal à 1",
				},
			];
		}

		return [];
	}

	/** L'identifiant de la ligne d'origine PES doit être nul (égal à 0). */
	checkIdLigneOriginePesNul(ligne: LigneMouvement): ErrorField[] {
		if (ligne.idLigneOriginePes !== 0) {
			return [
				{
					id: `ligneMouvement-idLigneOriginePes-${ligne?.identifiant}`,
					message: "L'identifiant de la ligne d'origine PES doit être égal à 0",
				},
			];
		}

		return [];
	}

	/** L'identifiant de la ligne d'origine PES doit être renseigné (supérieur ou égal à 1). */
	checkIdLigneOriginePesRenseigne(ligne: LigneMouvement): ErrorField[] {
		if (ligne.idLigneOriginePes < 1) {
			return [
				{
					id: `ligneMouvement-idLigneOriginePes-${ligne?.identifiant}`,
					message:
						"L'identifiant de la ligne d'origine PES doit être supérieur ou égal à 1",
				},
			];
		}

		return [];
	}
	// #endregion
}

export default BaseProfilRattachement;
